import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, Injectable, isDevMode, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateParser
} from '@ngx-translate/core';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport} from '@angular/common/http';
import {JWT_OPTIONS, JwtModule} from '@auth0/angular-jwt';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MaterialModule} from './material.module';
import {ScrollingModule} from '@angular/cdk/scrolling';
import {PipesModule} from './pipes.module';
import {DirectivesModule} from './directives.module';
import {LoginComponent} from './modules/login/login.component';
import {ResetPasswordComponent} from './modules/login/reset-password/reset-password.component';
import {ChangePasswordComponent} from './modules/login/change-password/change-password.component';
import {LayoutComponent} from './layout/layout.component';
import {BreadcrumbComponent} from './components/breadcrumb/breadcrumb.component';
import {StatusComponent} from './components/status/status.component';
import {BlankComponent} from './components/blank/blank.component';
import {AccessDeniedComponent} from './components/access-denied/access-denied.component';
import {ConfirmDialogComponent} from './components/confirm-dialog/confirm-dialog.component';
import {CartService} from './services/cart/cart.service';
import {StorageService} from './services/storage/storage.service';
import {RouteService} from './services/route/route.service';
import {SnackService} from './services/snack/snack.service';
import {EventService} from './services/event/event.service';
import {AuthService} from './services/auth/auth.service';
import {CentrePropertyService} from "./services/centre-property/centre-property.service";
import {UtilService} from './services/util/util.service';
import {ViewService} from './services/view/view.service';
import {FiscalService} from './services/fiscal/fiscal.service';
import {CashService} from './services/cash/cash.service';
import {RefundService} from './services/refund/refund.service';
import {ApiModule as SalesTerminalApi, Configuration as SalesTerminalConf} from './generated/sales-terminal-client';
import {
  ApiModule as TranslationApi,
  Configuration as TranslationConf, GeneralTranslation,
  TranslationService
} from './generated/translation-client';
import {ApiModule as ReportApi, Configuration as ReportConf} from './generated/report-client';
import {ApiModule as ReportAccountingApi, Configuration as ReportAccountingConf} from './generated/report-accounting-client';
import {ApiModule as CustomerApi, Configuration as CustomerConf} from './generated/customer-client';
import {ApiModule as ImageDownloadApi, Configuration as ImageDownloadConf} from './generated/s3-download-client';
import {environment} from '../environments/environment';
import {SalespointRoleComponent} from './components/salespoint-role-component/salespoint-role.component';
import {BookingsService} from './services/bookings/bookings.service';
import {CartMenuComponent} from './layout/menus/cart-menu/cart-menu.component';
import {UserMenuComponent} from './layout/menus/user-menu/user-menu.component';
import {FiscalMenuComponent} from './layout/menus/fiscal-menu/fiscal-menu.component';
import {NavMenuComponent} from './layout/menus/nav-menu/nav-menu.component';
import {RoleSwitchingComponent} from './modules/login/role-switching/role-switching.component';
import {ConfirmEmailComponent} from './modules/login/confirm-email/confirm-email.component';
import {MyProfileComponent} from './components/my-profile/my-profile.component';
import {PendingChangesDialogComponent} from './components/pending-changes-dialog/pending-changes-dialog.component';
import {ScrapService} from './services/scrap/scrap.service';
import {UserService} from './services/user/user.service';
import {SelfServiceEventService} from "./services/self-service/event/self-service-event.service";
import {SharedModule} from './shared/shared.module';
import {CountryService} from './services/country/country.service';
import {BackOfficeModule} from './modules/back-office/back-office.module';
import {ServiceWorkerModule} from '@angular/service-worker';
import {ProxiedLoginService, ProxiedPasswordService, ProxiedRegistrationService, ProxiedUsersService} from "./services/proxy/proxy.service";
import {NoopInterceptor} from "./interceptors/http-noop-interceptor";
import {OauthComponent} from "./modules/login/oauth/oauth.component";
import {StringUtilService} from "./services/util/string-util.service";
import {Observable, ReplaySubject, share, tap, map} from "rxjs";
import {SharedUtilService} from "./services/util/shared-util.service";

export function apiModuleConfigurationFactory() {
  return new SalesTerminalConf({
    basePath: environment.apiUrl + '/sales-terminal',
    credentials: {'bearerAuth': ''}
  });
}

export function translationApiModuleConfigurationFactory() {
  return new TranslationConf({
    basePath: environment.apiUrl + '/translation',
    credentials: {'bearerAuth': ''}
  });
}


export function reportModuleConfigurationFactory() {
  return new ReportConf({
    basePath: environment.apiUrl + '/report',
    credentials: {'bearerAuth': ''}
  });
}

export function reportAccountingModuleConfigurationFactory() {
  return new ReportAccountingConf({
    basePath: environment.apiUrl + '/report',
    credentials: {'bearerAuth': ''}
  });
}

export function customerModuleConfigurationFactory() {
  return new CustomerConf({
    basePath: environment.apiUrl + '/customer',
    credentials: {'bearerAuth': ''}
  });
}

export function imageDownloadModuleConfigurationFactory() {
  let conf: { basePath?: string } = {};
  if (environment.proxy) {
    conf = {
      basePath: '/s3-download'
    }
  }
  return new ImageDownloadConf(conf);
}

export function jwtOptionsFactory() {
  return {
    tokenGetter: () => {
      return '';
    }
  };
}

@Injectable()
export class LoadChunkErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;
    const scriptLoadError = /Failed to load module script/;
    if (chunkFailedMessage.test(error.message) || scriptLoadError.test(error.message)) {
      window.location.reload();
    } else if (!environment.production) {
      console.error(error);
    }
  }
}

@Injectable()
export class CustomTranslateLoader implements TranslateLoader {
  constructor(protected translationService: TranslationService) {}

  getTranslation(lang: string): Observable<GeneralTranslation> {
    return this.translationService.getTranslations(lang, 'api_');
  }
}

@Injectable()
export class MissingTranslationHelper implements MissingTranslationHandler {
  constructor(protected translationService: TranslationService, protected translateParser: TranslateParser, protected sharedUtilService: SharedUtilService) {}

  handle(params: MissingTranslationHandlerParams) {
    if (params.interpolateParams) {
      return (params.interpolateParams as any)['Default'] || params.key;
    }

    const namespace = params.key.split('.');
    const currentLang = params.translateService.currentLang;

    if (!this.sharedUtilService.translationNamespacesLoaded[currentLang]) {
      this.sharedUtilService.translationNamespacesLoaded[currentLang] = {};
    }

    if (namespace[0] === 'classifiers') {
      const classifier = `${namespace[0]}.${namespace[1]}`;
      const status = this.sharedUtilService.translationNamespacesLoaded[currentLang][classifier];
      if (typeof status === 'undefined') {
        const $translations = this.translationService.getTranslations(currentLang, classifier).pipe(
          tap(translations => {
            params.translateService.setTranslation(currentLang, translations, true);
            this.sharedUtilService.translationNamespacesLoaded[currentLang][classifier] = true;
          }),
          share({
            connector: () => new ReplaySubject(1),
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: false
          })
        );

        this.sharedUtilService.translationNamespacesLoaded[currentLang][classifier] = $translations;

        return this.map($translations, params, namespace);
      } else if (typeof status !== 'boolean') {
        return this.map(status, params, namespace);
      }
    }

    return params.key;
  }

  map(obs: Observable<GeneralTranslation>, params: MissingTranslationHandlerParams, namespace: string[]) {
    return obs.pipe(
      map(translations => this.translateParser.interpolate((translations as any)[namespace[0]][namespace[1]][namespace[2]], params.interpolateParams)),
      map((translation => translation === undefined ? params.key : translation))
    );
  }
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetPasswordComponent,
    ChangePasswordComponent,
    LayoutComponent,
    BreadcrumbComponent,
    StatusComponent,
    BlankComponent,
    AccessDeniedComponent,
    ConfirmDialogComponent,
    SalespointRoleComponent,
    CartMenuComponent,
    UserMenuComponent,
    FiscalMenuComponent,
    NavMenuComponent,
    RoleSwitchingComponent,
    ConfirmEmailComponent,
    MyProfileComponent,
    PendingChangesDialogComponent,
    OauthComponent
  ],
  imports: [
    ////////////////////////////////////////////////////////
    //
    // MODULES ARE LAZY LOADED, DO NOT IMPORT THEM HERE!!
    //
    ////////////////////////////////////////////////////////
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [TranslationService]
      }
    }),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory
      }
    }),
    TranslationApi.forRoot(translationApiModuleConfigurationFactory),
    SalesTerminalApi.forRoot(apiModuleConfigurationFactory),
    ReportApi.forRoot(reportModuleConfigurationFactory),
    ReportAccountingApi.forRoot(reportAccountingModuleConfigurationFactory),
    CustomerApi.forRoot(customerModuleConfigurationFactory),
    ImageDownloadApi.forRoot(imageDownloadModuleConfigurationFactory),
    BrowserAnimationsModule,
    MaterialModule,
    PipesModule,
    DirectivesModule,
    SharedModule,
    BackOfficeModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    AuthService,
    CentrePropertyService,
    ProxiedLoginService,
    ProxiedRegistrationService,
    ProxiedUsersService,
    ProxiedPasswordService,
    ViewService,
    RouteService,
    CartService,
    EventService,
    StorageService,
    SnackService,
    UtilService,
    FiscalService,
    CashService,
    RefundService,
    BookingsService,
    ScrapService,
    UserService,
    CountryService,
    SelfServiceEventService,
    StringUtilService,
    {
      provide: MissingTranslationHandler,
      useClass: MissingTranslationHelper,
      deps: [TranslationService, TranslateParser, SharedUtilService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoopInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: LoadChunkErrorHandler
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())
  ],
  bootstrap: [AppComponent]
})

export class AppModule {}
